import { jsx as _jsx } from "react/jsx-runtime";
import Highcharts from "highcharts";
import { useSignal } from "@preact/signals";
import merge from "@tinkoff/utils/object/mergeDeep";
import BaseChart from "../BaseChart";
import useMessageChannel from "../../../lib/hooks/use-message-channel";
import seriesCompare from "../../../lib/transformers/series-compare";
import seriesAddPrevLatestToName from "../../../lib/transformers/series-compare-add-prev-latest-to-name";
import { EVENT_UPDATE_NOTE } from "../../ModalNote";
import { getChartBudget, budgetIsForSeries } from "../../../lib/chart";
import asArray from "../../../lib/as-array";
import * as Series from "../../../lib/series";
import { secondsToMilliseconds } from "../../../lib/date";
export default function TimeSeriesChart(props) {
    const { transformers = [], type = "spline", yAxisLabelWidth } = props;
    const notesUpdates = useSignal(new Map());
    const channel = useMessageChannel();
    channel.subscribe(EVENT_UPDATE_NOTE, (note) => {
        notesUpdates.value = new Map(notesUpdates.value.set(note.id, note));
    });
    const applyChartOptions = (options) => {
        let dataSeries = (options.series || []);
        // Add budget zones to all series
        dataSeries = dataSeries.map(addBudgetZonesToSeries);
        // Ensure any series with only one point show the marker
        dataSeries.forEach((series) => {
            if (series.data.length === 1) {
                series.marker = {
                    enabled: true,
                    radius: 6,
                };
            }
        });
        const [xAxisMin, xAxisMax] = Series.getXAxisBoundsFromSeries(dataSeries);
        const notesSeries = getNotesSeries(options, notesUpdates.value);
        const deploysSeries = getDeploysSeries(options);
        const chartOptions = merge(options, {
            series: [
                ...getRateOfChangeSeries(dataSeries),
                ...processSeries(dataSeries),
                ...getAlertSeries(options),
                ...notesSeries,
                ...deploysSeries,
            ],
            xAxis: [
                {
                    id: 0,
                    type: "datetime",
                    tickLength: 0,
                    labels: {
                        y: 30,
                        style: {
                            color: "var(--high-emphasis)",
                        },
                        useHTML: true,
                    },
                    title: {
                        text: null,
                        style: {
                            color: "var(--medium-emphasis)",
                        },
                    },
                    min: xAxisMin,
                    max: xAxisMax,
                    lineColor: "var(--axis-grid-line-color)",
                },
                {
                    type: "datetime",
                    id: Series.getNotesXAxisId(),
                    plotLines: getXAxisPlotLines([
                        ...notesSeries,
                        ...deploysSeries,
                    ]),
                    labels: { enabled: false },
                    tickLength: 0,
                    lineWidth: 0,
                    min: xAxisMin,
                    max: xAxisMax,
                },
            ],
            yAxis: addYAxisPlotLines(options),
            chart: {
                events: {
                    render: function (e) {
                        if (e.target instanceof Highcharts.Chart) {
                            const chart = e.target;
                            // Move points in the notes, deploys, and alerts series to be below the X axis
                            const moveSeries = [
                                Series.getNotesSeriesId(),
                                Series.getDeploysSeriesId(),
                                Series.getAlertsSeriesId(),
                            ];
                            chart.series.forEach((s) => {
                                if (s.userOptions.id && moveSeries.includes(s.userOptions.id)) {
                                    s.points.forEach((point) => {
                                        var _a;
                                        (_a = point.graphic) === null || _a === void 0 ? void 0 : _a.attr({
                                            transform: "translate(0, 7)",
                                        });
                                    });
                                }
                            });
                            // Hide the notes/deploys plot lines by default
                            const notesAxisId = Series.getNotesXAxisId();
                            const notesXAxis = chart.xAxis.find((axis) => axis.userOptions.id === notesAxisId);
                            notesXAxis === null || notesXAxis === void 0 ? void 0 : notesXAxis.plotLinesAndBands.forEach((line) => {
                                line.svgElem.hide();
                                if (line.label) {
                                    line.label.hide();
                                }
                            });
                        }
                    },
                },
            },
            plotOptions: {
                series: {
                    states: { hover: { lineWidthPlus: 0, halo: { size: 0, opacity: 0 } } },
                    point: {
                        events: {
                            mouseOver: function (e) {
                                if (e.target instanceof Highcharts.Point) {
                                    const point = e.target;
                                    const plotLines = point.series.xAxis.plotLinesAndBands;
                                    plotLines.forEach((line) => {
                                        const options = line.options;
                                        // show the line that matches this point
                                        if (options.value === point.x) {
                                            line.svgElem.show();
                                            if (line.label) {
                                                line.label.show();
                                            }
                                        }
                                    });
                                }
                            },
                            mouseOut: function (e) {
                                if (e.target instanceof Highcharts.Point) {
                                    const point = e.target;
                                    const plotLines = point.series.xAxis.plotLinesAndBands;
                                    plotLines.forEach((line) => {
                                        // hide all lines
                                        line.svgElem.hide();
                                        if (line.label) {
                                            line.label.hide();
                                        }
                                    });
                                }
                            },
                        },
                    },
                    marker: {
                        states: {
                            hover: {
                                lineWidthPlus: 0,
                            },
                        },
                    },
                },
            },
        });
        const pvsColumnSeries = dataSeries.find((series) => series.type === "column");
        if (pvsColumnSeries !== undefined) {
            pvsColumnSeries.yAxis = "sample-size";
            chartOptions.yAxis.push({ id: "sample-size", visible: false });
            chartOptions.plotOptions.column = {
                borderWidth: 0,
                enableMouseTracking: false,
                states: {
                    inactive: {
                        opacity: 1,
                    },
                },
                zIndex: -1,
                yAxis: "sample-size",
                groupPadding: 0,
                pointPadding: 0.1,
            };
        }
        if (yAxisLabelWidth && chartOptions.chart) {
            chartOptions.chart.marginLeft = yAxisLabelWidth;
        }
        if (type === "areaspline") {
            return merge(chartOptions, {
                chart: {
                    type: "areaspline",
                },
                plotOptions: {
                    areaspline: {
                        fillOpacity: 0.2,
                    },
                    series: {
                        stacking: "normal",
                        lineWidth: 3,
                        marker: {
                            symbol: "circle",
                            radius: 0,
                            states: {
                                hover: {
                                    radius: 6,
                                },
                            },
                        },
                        states: {
                            hover: {
                                lineWidthPlus: 0,
                                opacity: 1,
                            },
                        },
                    },
                },
            });
        }
        return chartOptions;
    };
    // Add a type to all series so trend lines can be added
    const addType = (series) => series.map((s) => (Object.assign(Object.assign({}, s), { type: s.type || type })));
    // Always apply the compare transform to timeseries charts
    transformers.push(addType, seriesCompare, seriesAddPrevLatestToName);
    return (_jsx(BaseChart, Object.assign({ extending: true, enableMultipleYAxes: true }, props, { transformers: transformers, type: type, finalChartOptions: applyChartOptions })));
}
function getNotesSeries(options, notesUpdates) {
    const series = getSeries(options);
    const notes = getNotesByType(series, "note");
    if (Object.keys(notes).length === 0) {
        return [];
    }
    notesUpdates.forEach((update) => {
        if (update.action && update.action === "delete") {
            delete notes[update.id];
        }
        else {
            notes[update.id] = Object.assign(Object.assign({}, update), { time: secondsToMilliseconds(update.time) });
        }
    });
    return [
        {
            name: "Notes",
            id: Series.getNotesSeriesId(),
            type: "line",
            data: Object.values(notes)
                .sort((a, b) => a.time - b.time)
                .map((note) => ({
                x: note.time,
                y: 0,
                id: String(note.id),
                sites: note.sites,
                name: note.name,
                description: note.description,
            })),
            metricQualified: "",
            suffix: "",
            lineWidth: 0,
            includeInDataExport: false,
            xAxis: Series.getNotesXAxisId(),
            marker: notesAndDeploysMarker("circle"),
        },
    ];
}
function getDeploysSeries(options) {
    const series = getSeries(options);
    const deploys = getNotesByType(series, "deploy");
    if (Object.keys(deploys).length === 0) {
        return [];
    }
    return [
        {
            name: "Deploys",
            id: Series.getDeploysSeriesId(),
            type: "line",
            data: Object.values(deploys).map((deploy) => ({
                x: deploy.time,
                y: 0,
                id: String(deploy.snapshots[0]), // deploy details page uses the first snapshot_id
                sites: deploy.sites,
                name: deploy.name,
                description: deploy.description,
            })),
            metricQualified: "",
            suffix: "",
            lineWidth: 0,
            includeInDataExport: false,
            xAxis: Series.getNotesXAxisId(),
            marker: notesAndDeploysMarker("square"),
        },
    ];
}
function notesAndDeploysMarker(symbol) {
    return {
        enabled: true,
        radius: 4,
        fillColor: "rgba(127, 127, 127, 0.7)",
        opacity: 0.5,
        symbol,
        states: { hover: { lineWidthPlus: 0, fillColor: "rgba(127, 127, 127, 1)" } },
    };
}
function getNotesByType(series, type) {
    const allNotes = new Set(series.flatMap((s) => s.notes || []).filter((item) => item.type === type));
    const notesById = Object.fromEntries([...allNotes.values()].map((note) => [note.id, note]));
    return notesById;
}
// Generate a new series of data for the chart from the alerts data received.
function getAlertSeries(options) {
    var _a;
    const series = getSeries(options);
    const alerts = ((_a = series[0]) === null || _a === void 0 ? void 0 : _a.alerts) || [];
    if (alerts.length === 0) {
        return [];
    }
    return [
        {
            name: "Alerts",
            id: Series.getAlertsSeriesId(),
            type: "line",
            data: alerts.map((alert) => {
                // Calculate the colour of the point based on the alert status.
                let color = "rgba(127,127,127,1)";
                if (alert.status === "under") {
                    color = "rgba(116, 182, 102, 1)";
                }
                else if (alert.status === "over") {
                    color = "rgba(213, 74, 67, 1)";
                }
                return {
                    id: String(alert.alert_id),
                    color,
                    x: alert.timestamp * 1000,
                    y: 0,
                    alertData: alert,
                };
            }),
            metricQualified: "",
            color: "rgba(213, 74, 67, 1)",
            suffix: "",
            lineWidth: 0,
            includeInDataExport: false,
            xAxis: Series.getNotesXAxisId(),
            marker: {
                enabled: true,
                opacity: 0.5,
                symbol: "square",
                states: { hover: { lineWidthPlus: 0, opacity: 1 } },
            },
        },
    ];
}
// Generate a new series for the chart that render the rate of change thresholds as areasplines.
function getRateOfChangeSeries(series) {
    if (!isRateOfChangeBudgetChart(series)) {
        return [];
    }
    const statusSeries = getBudgetStatusSeries(series);
    const lowerSeries = [];
    const upperSeries = [];
    series.forEach((thisSeries) => {
        const lowerThresholdSeries = [];
        const upperThresholdSeries = [];
        thisSeries.data.forEach((dataPoint) => {
            if (typeof dataPoint.threshold_lower !== "undefined") {
                lowerThresholdSeries.push({
                    x: dataPoint.x,
                    y: dataPoint.threshold_lower,
                });
            }
            if (typeof dataPoint.threshold_upper !== "undefined") {
                upperThresholdSeries.push({
                    x: dataPoint.x,
                    y: dataPoint.threshold_upper,
                });
            }
        });
        const [lowerSeriesId, upperSeriesId] = Series.getRateOfChangeThresholdSeriesIds(thisSeries);
        const visible = thisSeries === statusSeries;
        lowerSeries.push({
            data: lowerThresholdSeries,
            id: lowerSeriesId,
            type: "areaspline",
            showInLegend: false,
            visible,
            enableMouseTracking: false,
            name: thisSeries.name + " lower threshold",
            color: thisSeries.budgetReverse ? "var(--bad-color)" : "var(--good-color)",
            fillOpacity: 0.35,
            lineWidth: 0,
            states: {
                inactive: {
                    enabled: false,
                },
            },
        });
        upperSeries.push({
            data: upperThresholdSeries,
            threshold: Infinity,
            id: upperSeriesId,
            type: "areaspline",
            showInLegend: false,
            enableMouseTracking: false,
            visible,
            name: thisSeries.name + " upper threshold",
            color: thisSeries.budgetReverse ? "var(--good-color)" : "var(--bad-color)",
            fillOpacity: 0.35,
            lineWidth: 0,
            states: {
                inactive: {
                    enabled: false,
                },
            },
        });
    });
    return [...lowerSeries, ...upperSeries];
}
function processSeries(series) {
    if (!isRateOfChangeBudgetChart(series)) {
        return series;
    }
    const statusSeries = getBudgetStatusSeries(series);
    return series.map((dataSeries) => {
        return Object.assign(Object.assign({}, dataSeries), { 
            // For charts with a rate of change budget, make sure only one series is visible.
            visible: dataSeries === statusSeries, 
            // We also force all series to be the same colour.
            color: "var(--series-color-1)" });
    });
}
function isRateOfChangeBudgetChart(series) {
    var _a, _b, _c, _d;
    return ((_d = (_c = (_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.budget) === null || _c === void 0 ? void 0 : _c.threshold) === null || _d === void 0 ? void 0 : _d.type) === "rate_of_change";
}
/**
 * Get the series that represents the budget status for the chart.
 */
function getBudgetStatusSeries(series) {
    var _a, _b;
    const budgetStatus = (_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.budget) === null || _b === void 0 ? void 0 : _b.status;
    const seriesName = budgetStatus === null || budgetStatus === void 0 ? void 0 : budgetStatus.series_name;
    if (!seriesName) {
        // No budget status series, so return the first series
        return series[0];
    }
    return series.find((s) => s.name === seriesName) || series[0];
}
function addBudgetZonesToSeries(series) {
    var _a, _b, _c;
    if ((_a = series.settings) === null || _a === void 0 ? void 0 : _a.budget) {
        const budget = series.settings.budget;
        if (budgetIsForSeries(budget, series) && ((_b = budget.threshold) === null || _b === void 0 ? void 0 : _b.type) === "absolute") {
            const badColor = series.budgetReverse ? "var(--bad-color)" : series.color;
            const goodColor = series.budgetReverse ? series.color : "var(--bad-color)";
            return Object.assign(Object.assign({}, series), { zones: [{ value: (_c = budget.threshold) === null || _c === void 0 ? void 0 : _c.value, color: badColor }, { color: goodColor }] });
        }
    }
    return series;
}
function getXAxisPlotLines(series) {
    const notesAndDeploys = series.flatMap((s) => s.data);
    const allXValues = notesAndDeploys.map((s) => s.x);
    // work out the middle time of the data series so we can swap label alignment
    const start = Math.min(...allXValues);
    const end = Math.max(...allXValues);
    const middleTime = start + (end - start) / 2;
    return notesAndDeploys.map((note) => {
        let align = "right";
        let alignX = -5;
        if (note.x < middleTime) {
            align = "left";
            alignX = 5;
        }
        return {
            className: "chart__note-label",
            value: note.x,
            color: "var(--low-emphasis)",
            label: {
                text: note.name,
                rotation: 0,
                x: alignX,
                y: 10,
                textAlign: align,
                style: {
                    color: "var(--high-emphasis)",
                },
            },
        };
    });
}
function addYAxisPlotLines(options) {
    var _a, _b, _c, _d;
    const yAxes = asArray(options.yAxis);
    const series = getSeries(options);
    const budget = getChartBudget(series);
    if (!budget) {
        // No budgets, so no need to modify the Y axes
        return yAxes;
    }
    // Having a line width of less than 2 results in some lines not being rendered. I don't know why.
    const BUDGET_LINE_WIDTH = 2;
    const budgetPlotLine = {
        color: "var(--bad-color)",
        width: BUDGET_LINE_WIDTH,
        value: ((_a = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _a === void 0 ? void 0 : _a.type) === "absolute" ? (_b = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _b === void 0 ? void 0 : _b.value : null,
    };
    if (yAxes.length === 1) {
        // With only 1 Y axis, we can assign the plot lines to the first axis
        return [
            merge(yAxes[0], {
                plotLines: [budgetPlotLine],
                minRange: ((_c = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _c === void 0 ? void 0 : _c.type) === "absolute" ? (_d = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _d === void 0 ? void 0 : _d.value : null,
            }),
        ];
    }
    // When we have multiple Y axes, we need to find which axis the budget belongs to
    const budgetSeries = series.find((s) => budgetIsForSeries(budget, s));
    if (!budgetSeries || !budgetSeries.yAxis) {
        // The budget series has no data, or we don't know which Y axis it belongs to
        return yAxes;
    }
    return yAxes.map((yAxis) => {
        var _a, _b;
        if (yAxis.id === budgetSeries.yAxis) {
            return merge(yAxis, {
                plotLines: [budgetPlotLine],
                minRange: ((_a = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _a === void 0 ? void 0 : _a.type) === "absolute" ? (_b = budget === null || budget === void 0 ? void 0 : budget.threshold) === null || _b === void 0 ? void 0 : _b.value : null,
            });
        }
        return yAxis;
    });
}
function getSeries(options) {
    if (options.series && options.series.length) {
        return options.series;
    }
    return [];
}
